// 던전히어로 튜닝 상수. 정본은 docs/COMBAT-FEEL.md, docs/AV-SPEC.md, docs/ART-BIBLE.md.
// 값을 바꿀 때는 문서를 먼저 고치고 여기를 맞춘다. 모든 시간 값은 60Hz 틱 단위다.
import { KAEL_PULL, KAEL_SKILLS, KAEL_SKILLS_BY_JOB, KAEL_TUNING } from './skills/kael';
import { LYRA_AIM, LYRA_AWAKEN, LYRA_COMBO, LYRA_SKILLS, LYRA_SKILLS_BY_JOB } from './skills/lyra';
import type { ArrowSpec, SkillHeroId, SkillNo, SkillKey, SkillTimeline } from './skills/types';
import { TICK_HZ, ms } from './ticks';

export { TICK_HZ, ms };
export const FIXED_DT = 1 / TICK_HZ;
export const deg = (v: number): number => (v * Math.PI) / 180;

/** 기본 공격 한 타의 규격. 시간은 틱. chainWindow는 판정 종료 시점부터 센다. */
export interface AttackSpec {
  readonly windup: number;
  readonly active: number;
  readonly recovery: number;
  readonly chainWindow: number;
  /** 판정 반경(m) */
  readonly range: number;
  /** 판정 부채꼴 전체 각(rad). 2π면 원형 */
  readonly arc: number;
  /** 공격력 배율 */
  readonly mult: number;
  readonly finisher: boolean;
  /** 공격 중 앞으로 미끄러지는 거리(m) */
  readonly lunge: number;
  /** 원거리 기본 공격(c022 라이라): 판정 첫 틱에 이 화살을 쏜다. 있으면 근접 부채꼴 판정을 하지 않는다 */
  readonly arrow?: ArrowSpec;
}

/**
 * COMBAT-FEEL 1장: Kael 4타 연계.
 * lunge(m): 선딜+판정 동안 게임 위치가 나아가는 거리. 카엘 클립이 구간 동안 실제로 나아가는 몸 이동(클립 표 rootNetM:
 * 1타 -0.15, 2타 0.44, 3타 1.03, 4타 1.23, 2026-10-14)에 맞춘다. 다르면 공격이 끝날 때 보이는 몸이 그 차이만큼 미끄러진다.
 */
export const COMBO: readonly AttackSpec[] = [
  { windup: ms(80), active: ms(60), recovery: ms(150), chainWindow: ms(300), range: 2.7, arc: deg(150), mult: 1.0, finisher: false, lunge: 0 },
  { windup: ms(70), active: ms(60), recovery: ms(160), chainWindow: ms(300), range: 2.7, arc: deg(150), mult: 1.05, finisher: false, lunge: 0.45 },
  { windup: ms(90), active: ms(80), recovery: ms(220), chainWindow: ms(350), range: 3.0, arc: deg(210), mult: 1.3, finisher: false, lunge: 1.05 },
  { windup: ms(120), active: ms(120), recovery: ms(320), chainWindow: 0, range: 3.6, arc: deg(360), mult: 2.2, finisher: true, lunge: 1.2 },
];

/** 참격 궤적 표시 시간(렌더). 측정 원작 66~100ms → 70~100ms */
export const SLASH_TRAIL_MS = { min: 70, max: 100 } as const;
/** VFX-REFERENCE 4.2: 흰 코어 참격선 33~66ms */
export const SLASH_CORE_MS = { min: 33, max: 66 } as const;

/** COMBAT-FEEL 2장 */
export const HITSTOP = { normal: ms(40), heavy: ms(70), boss: ms(90) } as const;
export const CAMERA_PUNCH = { bossPx: 6, bossMs: 80, maxPx: 8, decayMs: 80 } as const;
/** 일반 타격은 짧게 밀어 무리가 근접에 머물게 한다(2026-10-14: 1.2m는 몹을 사거리 밖으로 밀어 위협이 사라졌다) */
/** 마무리타: 맞은 무리를 부채꼴로 띄워 날려 보낸다(2026-10-14 피격 반응: 3.0m·1.6m는 진형 링 안에서 거의 안 보였다) */
export const KNOCKBACK = { normalDist: 0.45, normalStun: ms(220), finisherDist: 4.2, finisherLift: 2.4, finisherStun: ms(600) } as const;
/**
 * 공중 연타(COMBAT-FEEL 2.2): 뜬 몹을 다시 맞히면 현재 높이에서 다시 띄운다.
 * maxRelaunch: 한 번 띄운 뒤 재띄우기 상한. liftDecay: 재띄울 때마다 높이·체공에 곱하는 감쇠(높이 0.75^n, 체공 √높이).
 * pushDist: 재띄우기당 수평 밀림(m). damageMult: 공중 몹에 들어가는 타격의 피해 배율.
 */
export const JUGGLE = { maxRelaunch: 3, liftDecay: 0.75, pushDist: 0.5, damageMult: 1.15 } as const;
/**
 * 피격 상태 5종(COMBAT-FEEL 2.7): 서기 → 경직 → 띄움 → 다운 → 기상.
 * downTicks: 다운 길이. wakeTicks: 기상 무적 길이(0.6초). downDamageMult: 다운 중 추격 피해 배율.
 * airCapTicks: 같은 대상의 체공 누적 상한(넘으면 강제 다운). eliteResist: 엘리트가 띄움·다운을 막을 확률.
 * forcedFallSpeed: 강제 다운 때 공중에서 떨어지는 속도(m/s).
 */
export const HIT_STATE = { downTicks: ms(900), wakeTicks: ms(600), downDamageMult: 0.6, airCapTicks: ms(3000), eliteResist: 0.5, forcedFallSpeed: 14 } as const;
/** 회피 캔슬 쿨: 공격·스킬을 회피로 끊으면 이만큼 지나야 다시 끊을 수 있다(COMBAT-FEEL 2.8) */
export const DODGE = { dist: 4, duration: ms(350), iframes: ms(280), cooldown: ms(600), cancelCooldown: ms(3000) } as const;
/**
 * COMBAT-FEEL 2장: 영웅 피격 경직·피격 후 무적, 선입력 버퍼(틱).
 * armorGrace: 공격·스킬 중(행동 아머)에 맞으면 경직 없이 이만큼만 보호한다. 군중 속 연타가 공짜가 되지 않게 짧게 둔다.
 */
export const HURT = { stun: ms(180), grace: ms(500), armorGrace: ms(300) } as const;
export const INPUT_BUFFER = ms(133);
/** 경고 공격은 사거리의 이 비율 안에서 시작한다(중심점 판정과 짝) */
export const TELEGRAPH_TRIGGER_RATIO = 0.85;

export const HERO = {
  radius: 0.45,
  runSpeed: 7.0,
  // 2026-10-20 대표님 지시 "초반에는 10단위, 피통은 백단위, 템에 따라 크게 성장, 맥뎀 99999": 공격 2000→40(1/50), 체력 18420→360.
  // 몹 체력·보스 체력은 공격과 같은 1/50, 영웅이 받는 피해는 체력과 같은 비율(360/18420)로 줄여 타격 수·생존 시간은 그대로 둔다(LIVE-FUN-PLAN 3.0).
  maxHp: 360,
  // 2026-10-28 대표님 피드백 "초보 때 MP가 빠르게 닳아 평타로 잡는 재미, 스킬은 절제하는 느낌"(docs/BALANCE-1028.md 1절):
  // Lv1 최대 MP 320→150(스킬 3~4번), 초당 회복 12→3. 레벨당 +3 MP·회복 +2%(growth LEVEL_STATS), 평타 적중이 MP를 채운다(MP_ECON).
  maxMp: 150,
  /** 초당 MP 회복(Lv1 기준, 레벨·트리·장비 배율과 유니크 고정 회복이 더해진다) */
  mpRegen: 3,
  attack: 40,
  critChance: 0.15,
  critMult: 1.8,
  /** 데미지 무작위 폭 ±8% */
  damageJitter: 0.08,
  /** 공중에서 도약 스킬(붉은 낙화)이 캔슬돼 남은 높이를 바닥까지 내려올 때의 일정 낙하 속도(m/s). 2.5m 높이면 약 13틱 */
  airFallSpeed: 12,
} as const;

/**
 * MP 리듬(docs/BALANCE-1028.md 1절): 기본 공격 한 동작이 처음 맞힌 순간 MP를 채운다(맞힌 대상 수와 무관, 동작당 1회).
 * swing: 1~3타, finisher: 마무리타(4타). 콤보 한 바퀴(1.53초) 15 = 초당 약 9.8. 유니크 '평타로 얻는 MP'(basicMpMult)가 곱해진다.
 * 2026-11-02 대표님 "엠피가 너무 모자르다, 차라리 전이 나았어" → +2/+4에서 +3/+6(BALANCE-1028 9절). 초보 분당 스킬은 1028(16)과 그 전(22) 사이 19, 평타로 채워 쓰는 리듬은 유지.
 */
export const MP_ECON = { swing: 3, finisher: 6 } as const;

/**
 * 공격 자석(COMBAT-FEEL 2.3): 기본 공격·스킬 시작 때 방향을 적에게 붙인다.
 * radius: 탐색 반경(m, 몸 가장자리 기준). coneDeg: 스틱을 잡았을 때 스틱 방향 좌우 허용각(도).
 * stopGap: 기본 공격 돌진이 대상 몸 가장자리 앞에서 멈추는 간격(m).
 */
export const ATTACK_MAGNET = { radius: 5.5, coneDeg: 60, stopGap: 0.1 } as const;

/** 한 번의 피해 상한(대표님 2026-10-20 "맥뎀이 99999"). 장비·강화·전직으로 커져도 이 값을 넘지 않는다 */
export const DAMAGE_CAP = 99999;

/**
 * 스킬 U/I/O/L/P. 4번째(진홍 초승달)는 Lv10, 5번째(천공 올려베기)는 Lv5부터 자동 습득해 쓴다(트리 노드는 강화). id는 1부터, 입력 Btn.Skill1..5.
 * 2026-10-22: 수치의 정본은 스킬 타임라인 데이터(src/shared/skills/kael.ts, COMBAT-FEEL 2.10)다. 여기는 그 데이터에서 만든 표다.
 * mult·radius는 첫 히트박스에서: 원 반경 · 부채꼴 사거리 · 경로·투사체 폭의 절반.
 */
export interface SkillSpec {
  readonly id: SkillNo;
  readonly key: SkillKey;
  readonly name: string;
  readonly cooldown: number;
  readonly mpCost: number;
  readonly mult: number;
  readonly radius: number;
  readonly duration: number;
}
function skillSpecOf(t: SkillTimeline): SkillSpec {
  const hb = t.hitboxes[0], sh = hb.shape;
  const radius = sh.kind === 'circle' ? sh.radius : sh.kind === 'cone' ? sh.range : sh.kind === 'arrow' ? sh.arrow.width / 2 : sh.kind === 'zone' ? sh.zone.radius : sh.width / 2;
  return { id: t.id, key: t.key, name: t.name, cooldown: t.cooldown, mpCost: t.mpCost, mult: hb.mult, radius, duration: t.duration };
}
export const SKILLS: readonly SkillSpec[] = KAEL_SKILLS.map(skillSpecOf);
/** 라이라 스킬 표(c022). 카엘은 SKILLS 그대로 */
export const LYRA_SKILL_SPECS: readonly SkillSpec[] = LYRA_SKILLS.map(skillSpecOf);
/** 라이라 전직 갈래별 스킬 표(C10-6, 1..13 — 12·13 = 전직 스킬). 앞 11개는 LYRA_SKILL_SPECS와 같은 값 */
export const LYRA_SKILL_SPECS_BY_JOB: readonly [readonly SkillSpec[], readonly SkillSpec[]] = [LYRA_SKILLS_BY_JOB[0].map(skillSpecOf), LYRA_SKILLS_BY_JOB[1].map(skillSpecOf)];
/** 영웅별 스킬 사양(스킬 번호 n = 표[n-1]). 모르는 영웅은 카엘 */
/** 카엘 전직 갈래별 스킬 표(C10-5, 1..10 — 9·10 = 전직 스킬). 앞 8개는 SKILLS와 같은 값 */
export const KAEL_SKILL_SPECS_BY_JOB: readonly [readonly SkillSpec[], readonly SkillSpec[]] = [KAEL_SKILLS_BY_JOB[0].map(skillSpecOf), KAEL_SKILLS_BY_JOB[1].map(skillSpecOf)];
/** 영웅 스킬 표. 전직 갈래 job(전직 전 0)에 따라 카엘 9·10번(0 블러드 버서커 · 1 소드 마스터)·라이라 12·13번(0 윈드 스나이퍼 · 1 트랩 레인저)이 다르다 */
export function heroSkillSpecs(hero: SkillHeroId | undefined, job: 0 | 1 = 0): readonly SkillSpec[] {
  return (hero === 'lyra' ? LYRA_SKILL_SPECS_BY_JOB : KAEL_SKILL_SPECS_BY_JOB)[job === 1 ? 1 : 0];
}
/** 영웅별 기본 공격 연계(카엘 4타 COMBO, 라이라 원거리 3타) */
export function heroCombo(hero: SkillHeroId | undefined): readonly AttackSpec[] {
  return hero === 'lyra' ? LYRA_COMBO : COMBO;
}
/** 영웅별 각성 난무: 타격 수·반경·한 번 배율(카엘 = AWAKEN 값, 라이라 폭풍 화살비) */
export function heroAwaken(hero: SkillHeroId | undefined): { readonly hits: number; readonly radius: number; readonly mult: number } {
  return hero === 'lyra' ? LYRA_AWAKEN_HITS : KAEL_AWAKEN_HITS;
}
/** 영웅별 공격 자석 반경(m). 라이라는 원거리라 넓다 */
export function heroMagnetRadius(hero: SkillHeroId | undefined): number {
  return hero === 'lyra' ? LYRA_AIM.radius : ATTACK_MAGNET.radius;
}

/** COMBAT-FEEL 1.1: 스킬 세부 동작 [설계]. 정본은 skills/kael.ts의 KAEL_TUNING */
export const SKILL_TUNING = KAEL_TUNING;

/** COMBAT-FEEL 3장 */
export const MOB_CAPACITY = 60;
export const MOBS = {
  /** 2026-10-20 몹몰이(대표님 "몹을 몰아서 사냥할수 있게"): 9→13. 방에 들어서면 대부분이 영웅을 알아채 따라붙는다 */
  aggroRadius: 13,
  /** 한 번 추적을 시작한 몹은 이 거리까지 계속 따라온다(몹몰이: 달리면 따라 모여든다) */
  leashRadius: 18,
  onScreenTarget: [25, 40] as const,
  waveInterval: [ms(8000), ms(12000)] as const,
  massKillThreshold: 20,
  telegraphWarnToRed: ms(500),
  /** 등장 연출(Spawning) 길이 */
  spawnTicks: ms(400),
  /** 사망 연출(Dying) 길이 */
  dyingTicks: ms(900),
  /** 엘리트: 체력 배율, 렌더 크기 배율(같은 InstancedMesh 안에서 크기만 키운다) */
  elite: { hpMult: 3, scale: 1.4 },
  /**
   * 공격 결심(2026-10-14 전투 개편): 선딜이 이 비율을 넘긴 일반 몹은 기본공격(마무리타 제외)에 끊기지 않고 공격을 낸다.
   * 마무리타·스킬·각성은 언제든 끊는다. 연타만으로 무피격이 되지 않게 한다.
   */
  commitAfter: 0.3,
} as const;

/**
 * 웨이브 등장(2026-10-14 전투 개편): 방 가장자리 입구 2~3곳에서 줄지어 달려 들어온다.
 * 영웅 둘레에 한꺼번에 생기면 몰이·이동 없이 제자리 연타로 끝나기 때문이다.
 */
export const WAVE_SPAWN = {
  /** 입구 한 곳에서 다음 몹이 나오기까지(틱, 세계 시간) */
  interval: ms(90),
  /** 한 번에 입구마다 나오는 마릿수(무리로 달려든다) */
  perEntry: 3,
  /** 입구 수: 웨이브가 이 마릿수 이상이면 3곳, 아니면 2곳 */
  threeEntriesAt: 20,
  /** 입구는 영웅에게서 이 거리 이상 */
  minDistance: 9,
  /** 입구는 이 거리에 가까운 곳을 고른다(너무 멀면 첫 접촉까지 지루하다) */
  preferDistance: 12,
  /** 입구 사이 최소 간격(m) */
  separation: 7,
  /** 방 벽에서 안쪽으로 들인 거리(m) */
  inset: 2,
  /** 입구 둘레 흩어짐 반경(m) */
  jitter: 1.6,
} as const;

/**
 * 처치 반응(2026-10-14 전투 개편): 맞은 방향으로 튕겨 날아간다. speed 수평 m/s, up 수직 초속 m/s.
 * basic 일반 타격, heavy 마무리타·스킬, awaken 각성 난무.
 */
export const DEATH_LAUNCH = {
  basic: { speed: 6, up: 5 },
  // up은 사라지기(dyingTicks 0.9초) 전에 한 번 튀고 착지하는 범위 안에서 올린다
  heavy: { speed: 10.5, up: 7 },
  awaken: { speed: 12, up: 8.2 },
  gravity: 22,
  /** 땅에 닿을 때 수직 반발 계수 */
  bounce: 0.32,
  /** 땅에서 미끄러질 때 감속(1/s) */
  groundDrag: 7,
} as const;

/**
 * 조작 캔슬(2026-10-14 전투 개편, COMBAT-FEEL 2장): 회피는 공격 어느 단계든 끊는다. 스킬은 판정 중이 아니면 끊는다.
 * 이동은 공격 후딜의 이 비율이 지나면 끊는다. 스킬 중 회피의 60% 제한은 2026-10-21에 없애고 DODGE.cancelCooldown으로 대신했다(COMBAT-FEEL 2.8).
 */
export const CANCEL = { moveAfterRecovery: 0.45 } as const;

/**
 * 스킬 연계(LINK, docs/LIVE-FUN-PLAN.md 3.1 · 설계도 rule-skill-link).
 * cancelAt[i]: 스킬 i+1 사용 중 이 비율(actTick/duration) 이상이면 다른 스킬 입력으로 캔슬할 수 있다. 모든 스킬이 CANCEL_ZONE(40~60%) 안이다(COMBAT-FEEL 2.8).
 * window: 스킬이 끝나거나 캔슬된 뒤 다음 스킬을 이어 붙일 수 있는 틱. 기본 공격 적중이 다시 채운다.
 * damage[stage-1]: 연계 단계별 스킬 피해 배율. finishGauge: 3단 진입 때 한 번 더하는 각성 게이지.
 */
/** 스킬 캔슬 구간 허용 범위(길이 대비). 모든 스킬의 LINK.cancelAt이 이 안에 있어야 한다 */
export const CANCEL_ZONE = { min: 0.4, max: 0.6 } as const;
export const LINK = { cancelAt: KAEL_SKILLS.map((t) => t.cancelAt), window: ms(900), damage: [1, 1.2, 1.35], finishGauge: 0.15, maxStage: 3 } as const;
/** 라이라 스킬 캔슬 구간(c022). 카엘은 LINK.cancelAt 그대로 */
export const LYRA_CANCEL_AT: readonly number[] = LYRA_SKILLS.map((t) => t.cancelAt);
/** 라이라 전직 갈래별 캔슬 구간(C10-6, 1..13). 앞 11개는 LYRA_CANCEL_AT과 같다 */
export const LYRA_CANCEL_AT_BY_JOB: readonly [readonly number[], readonly number[]] = [LYRA_SKILLS_BY_JOB[0].map((t) => t.cancelAt), LYRA_SKILLS_BY_JOB[1].map((t) => t.cancelAt)];
/** 카엘 전직 갈래별 캔슬 구간(C10-5, 1..10). 앞 8개는 LINK.cancelAt과 같다 */
export const KAEL_CANCEL_AT_BY_JOB: readonly [readonly number[], readonly number[]] = [KAEL_SKILLS_BY_JOB[0].map((t) => t.cancelAt), KAEL_SKILLS_BY_JOB[1].map((t) => t.cancelAt)];

/**
 * 스킬 흡입(몹몰이, LIVE-FUN-PLAN 3.0): 칼날 폭풍은 도는 동안 반경 안 몹을 영웅 쪽으로 계속 당기고,
 * 붉은 낙화는 착지 직전 leadTicks 동안 반경 안 몹을 착지점(영웅 발밑)으로 모은다. 보스는 당기지 않는다.
 * speed는 m/s(세계 시간과 무관한 영웅 시간 기준), 영웅 몸에 닿으면 멈춘다(각성 모으기와 같은 규칙).
 */
export const SKILL_PULL = KAEL_PULL;

/** 방패 고블린 정면 막기: 기본공격(마무리타 제외)이 몹 정면 이 각도 안에서 오면 피해 배율만큼만 들어간다 */
export const SHIELD_GUARD = { halfArcDeg: 60, damageMult: 0.25 } as const;

/** 자세·가드 브레이크: 방패는 막힌 타격 횟수, 엘리트는 실제 피해 비율로 쌓인다. */
export const POSTURE = {
  shieldBlocked: 0.25,
  eliteDamageRatio: 1.5,
  finisherBonus: 0.15,
  skillBonus: 0.1,
  decayDelay: ms(1500),
  decayPerSecond: 0.2,
  breakTicks: ms(1200),
} as const;

/**
 * 일반 몹(이끼 고블린·멧돼지·주술사) 자세 증가량(COMBAT-FEEL 2.4). 방패·엘리트는 위 POSTURE의 기존 규칙을 쓴다.
 * 처형(EXECUTE): 무방비 몹을 기본 공격·스킬로 맞히면 남은 체력을 전부 깎는다. 보상은 한 동작당 maxRewardsPerAction회까지.
 */
export const POSTURE_NORMAL = { basic: 0.12, finisher: 0.5, skill: 0.4, awaken: 0.2 } as const;
export const EXECUTE = { healRatio: 0.02, awakenGain: 0.05, hitstop: HITSTOP.boss, punchPx: 5, punchMs: 90, maxRewardsPerAction: 3 } as const;

/**
 * 연속 처치(폭주, COMBAT-FEEL 2.5): 처치마다 window 타이머를 채우고, 사슬이 every의 배수가 될 때마다 MP·쿨다운 환급과 이동 속도 효과.
 * mpRefund: 지역 3 이상 최대 MP 비율(기본값), mpRefundByRegion: 지역 번호별 예외(지역 1·2 초보 구간은 5%, BALANCE-1028 5절). 읽을 때는 rampageMpRefund(region). cooldownRefund: 스킬 쿨다운 단축 틱. speedBonus: 달리기 속도 증가율, buffTicks: 지속.
 */
export const RAMPAGE = { window: ms(3000), every: 5, mpRefund: 0.1, mpRefundByRegion: { 1: 0.05, 2: 0.05 } as Readonly<Record<number, number>>, cooldownRefund: ms(2000), speedBonus: 0.1, buffTicks: ms(3000) } as const;
/** 폭주 MP 환급 비율(최대 MP 대비). region = 지역 번호(1~) 또는 'r1'~ 키. 지역 1·2 = 5%, 3 이상 = 10%, 마을·알 수 없는 값은 기본값(10%) */
export function rampageMpRefund(region: number | string): number {
  const no = typeof region === "number" ? region : Number(/^r(\d+)$/.exec(region)?.[1] ?? NaN);
  return RAMPAGE.mpRefundByRegion[no] ?? RAMPAGE.mpRefund;
}
/**
 * 스타일 랭크(COMBAT-FEEL 2.14, 2026-10-26 DivineStudi0s 릴 측정): 연속 처치 사슬로 정하는 표시 등급. 판정·보상·시간에는 영향이 없다.
 * thresholds[i] 이상이면 i+1등급(letters[i]). 사슬이 끊기면 0. promoMs: HUD 승급 연출 [링 폭발, 입자 흩어짐, 정착](실제 시간 ms, 합 450).
 */
export const STYLE_RANK = { thresholds: [5, 15, 30, 50] as readonly number[], letters: ['B', 'A', 'S', 'SS'] as readonly string[], promoMs: [50, 250, 150] as const } as const;
/** 대량 처치 슬로(COMBAT-FEEL 2.14): 한 동작 20마리 이상 처치 순간 세계(몹·보스)만 worldScale배로 slowTicks(영웅 틱) 늦춘다. 영웅·입력은 그대로 */
export const MASS_SLOW = { worldScale: 0.25, slowTicks: ms(280) } as const;

/** 전투방 정리 때 잃은 체력의 회복 비율. 보스방에는 적용하지 않는다. */
export const ROOM_HEAL = { missingRatio: 0.4 } as const;

/** 영웅의 마무리타·스킬이 처음 맞힌 순간 카메라 펀치(AV-SPEC: 단발, 최대 8px) */
export const HIT_PUNCH = { px: 3, ms: 70 } as const;
/** 일반 타격이 처음 맞힌 순간의 약한 카메라 펀치(action-feel-director 약타: 화면 높이 약 0.2%, 동작당 1회) */
export const LIGHT_PUNCH = { px: 1.6, ms: 60 } as const;
/**
 * 몹 피격 반응(렌더 전용, 판정·이동은 그대로): 맞는 순간 영웅 반대쪽으로 recoilM(m) 튕겼다가 recoilTicks 동안 돌아온다.
 * 히트스톱으로 멈춘 동안에는 맞은 몹만 좌우로 shakeM(m) 떤다(피격자 떨림, 카메라는 흔들지 않는다).
 * 2026-10-14 실화면: 일반 넉백 0.45m만으로는 맞아도 몹 무리가 움직이지 않아 타격이 안 느껴졌다.
 */
export const MOB_HIT_REACT = { recoilM: 0.42, recoilTicks: 14, shakeM: 0.07, boarScale: 0.35 } as const;
/**
 * 몹 진형(2026-10-14 대표님 요청 "고퀄리티 핵앤슬래시" 1단계): 근접 몹은 영웅 둘레 공격 자리(slots칸, slotRadius m)를 차지한 것만 붙고,
 * 나머지는 waitRadius 밖에서 영웅을 보며 기다린다. 자리가 비면(처치·공중으로 날아감) 가까운 대기 몹이 들어온다.
 * personalSpace: 몹끼리 밀어내는 반경 배율(몸 반경 × 이 값). 겉모습 폭이 충돌 반경보다 커서 1이면 몸이 겹쳐 보였다.
 * 이전에는 모두 영웅에게 0.97m까지 달려들어 20여 마리가 한 덩어리로 겹치고 영웅이 묻혔다(qa/combat/formation).
 */
export const MOB_FORMATION = { slots: 8, slotRadius: 1.55, waitRadius: 3.6, personalSpace: 1.45, reassignTicks: 6, arrive: 0.2, attackTokens: 3 } as const;

/**
 * 몹 종류별 기본 수치(설계 가안). 경고 원(주황) → 발동 0.5초 전 빨강(AV-SPEC).
 * attackTicks: 타격 뒤 Attack 상태를 유지하는 길이(휘두름 자세·후딜). 멧돼지는 이 동안 돌진한다.
 */
export const MOB_STATS = [
  { name: '이끼 고블린', hp: 104, speed: 3.8, radius: 0.42, damage: 12, attackRange: 1.3, windup: ms(500), attackTicks: ms(200), cooldown: ms(1100) },
  { name: '방패 고블린', hp: 196, speed: 3.2, radius: 0.5, damage: 16, attackRange: 1.4, windup: ms(650), attackTicks: ms(250), cooldown: ms(1400) },
  { name: '가시 멧돼지', hp: 250, speed: 2.8, radius: 0.7, damage: 31, attackRange: 6.0, windup: ms(900), attackTicks: ms(300), cooldown: ms(3000) },
  // R2 후열 원거리(docs/HUNT-R2.md 3절): attackRange = 포격 시작 거리, windup = 경고 원 길이. 맞으면 시전이 끊긴다(슈퍼아머 없음)
  { name: '잿버섯 주술사', hp: 92, speed: 3.0, radius: 0.45, damage: 27, attackRange: 11, windup: ms(1100), attackTicks: ms(300), cooldown: ms(3400) },
] as const;

/**
 * C2 몹 직군 수치(shared/mobRoles.ts 계약, game/mobs.ts가 읽는다). 고블린·주술사 몸 위에 덧씌워 공격 방식만 바꾼다.
 * 투척형: 영웅 위치 원 경고(windup) → 발사 → flightTicks 뒤 같은 원으로 판정(경고 시작부터 착탄까지 반응 시간 = windup + flightTicks).
 * 돌진형: 선 경고(windup) → dashTicks 동안 dashLength(m) 직선 돌진(멧돼지 6m/18틱보다 짧고 빠르다).
 * 소환형: 피해 없는 룬(windup) → 졸개 고블린 minions마리. 소환자당 maxCasts회·살아 있는 졸개 perSummoner, 방 전체 perRoom 상한.
 * 졸개(소환·분열)는 처치 EXP·드롭·목표 집계가 없고 방 정리 조건(싸울 수 있는 몹 0)에만 든다.
 */
export const MOB_ROLE_STATS = {
  thrower: { hpMult: 0.9, speedMult: 0.9, damage: 14, windup: ms(450), flightTicks: 30, radius: 1.5, range: 9, keepAway: 4.5, retreatMult: 0.8, cooldown: ms(2600), attackTicks: ms(250), maxCasting: 2, firstDelay: ms(900), arcHeight: 2.2, handY: 1.2 },
  rusher: { hpMult: 1, speedMult: 1.05, damage: 15, windup: ms(500), trigger: 3.2, dashLength: 3.6, dashTicks: ms(200), cooldown: ms(2200), width: 0.9 },
  summoner: { windup: ms(900), cooldown: ms(6000), runeRadius: 1.8, minions: 2, maxCasts: 2, perSummoner: 4, perRoom: 6, maxCasting: 1, firstDelay: ms(1200), minionHpMult: 0.7, spawnRing: 1.3 },
} as const;
/** 정예 접두 수치(elite=1만). 신속: 이동 ×speedMult·선딜 ×windupMult. 보호막: 최대 HP × shieldFrac 흡수막. 분열: 처치 때 작은 고블린 count마리(한 단계만) */
export const ELITE_AFFIX_STATS = {
  swift: { speedMult: 1.35, windupMult: 0.8 },
  bulwark: { shieldFrac: 0.35 },
  splitting: { count: 2, hpMult: 0.5, ring: 0.9 },
} as const;

/** 주술사 행동(R2). keepAway 안으로 영웅이 오면 물러나고, 포격은 시전 시작 때 영웅 위치에 고정된 원이다 */
export const SHAMAN = {
  keepAway: 6,
  /** 물러날 때 속도 배율 */
  retreatMult: 0.85,
  mortarRadius: 2.0,
  /** 등장 연출이 끝난 뒤 첫 포격까지 최소 대기 */
  firstCastDelay: ms(1500),
  /** 동시에 시전 중일 수 있는 주술사 수 */
  maxCasting: 2,
} as const;

/**
 * R2 위험 지대·공정성(docs/HUNT-R2.md 4절).
 * minResolveGap: R2의 경고 공격(분출구·주술사·대주술사 패턴)은 서로 이 간격 미만으로 판정 순간이 겹치지 않는다(회피 쿨다운 0.6초보다 길게).
 * roomGrace: 방에 들어가 문이 닫힌 뒤 이 시간 동안 분출구·주술사가 경고를 시작하지 않는다(포털·입장 직후 공격 금지).
 */
export const HAZARD = {
  ventPeriod: ms(6000),
  ventWindup: ms(1400),
  ventDamage: 29,
  /** 분출은 몹도 태운다(보스 제외). 고블린을 끌어들이면 한 번에 쓰러진다 */
  ventMobDamage: 120,
  lavaBurnInterval: ms(500),
  /** 화상 1회 = 최대 HP 비율 */
  lavaBurnRatio: 0.04,
  roomGrace: ms(2500),
  minResolveGap: ms(700),
} as const;

/**
 * 지역 상태 이상(D3a). 서리(R3): 서리 정예의 타격·서리 균열이 영웅을 둔화(이동 ×chillSpeed), 균열은 lavaBurnInterval마다 최대 HP frostBurnRatio 동상.
 * 중독(R4): 독 정예의 타격·독 늪이 중독을 걸고(poisonTicks 동안), poisonInterval마다 최대 HP poisonRatio 피해. 늪에 서 있으면 중독이 계속 새로 걸린다.
 * 피해는 받는 피해 배율·보호막·세트 버팀을 받는다(용암 화상과 같은 규칙, combat.burnHero)
 */
export const REGION_STATUS = {
  chillTicks: ms(1500),
  chillSpeed: 0.7,
  frostBurnRatio: 0.03,
  poisonTicks: ms(3000),
  poisonInterval: ms(500),
  poisonRatio: 0.015,
} as const;

/** COMBAT-FEEL 3.1: 1지역 보스는 Lv1 기준 60초 안팎(실측 파일럿 DPS 약 5,900 → 2026-10-20 수치 1/50로 약 118) */
export const BOSS = {
  name: '고목 수호자',
  hp: 6400,
  radius: 2.4,
  damage: 47,
  /** 최대 체력의 이 비율만큼 피해가 쌓일 때마다 경직 */
  staggerRatio: 0.1,
  /** 추적 속도(m/s)와 공격을 시작하는 거리(m) */
  chaseSpeed: 1.5,
  attackReach: 9,
  slamRadius: 4.5,
  sweepRange: 7,
  sweepArcDeg: 120,
  /** 체력 비율 70%·40%에서 고블린 6마리를 반지름 5m 원에 소환 */
  summonAt: [0.7, 0.4] as const,
  summonCount: 6,
  summonRing: 5,
} as const;

/**
 * R2 아레나 리더 잿불 대주술사(docs/HUNT-R2.md 3절). 쫓아오지 않고 거리를 재며 포격한다.
 * 패턴: 운석 연속(meteor*) → 균열 3줄(fissure*) 교대, 영웅이 novaTrigger 안이면 화염 고리(nova*) 우선.
 * 체력 summonAt마다 주술사 2 + 고블린 3 소환, ventsAt 이하에서 아레나 분출구가 깨어난다(VentDef.fromBossHp).
 */
export const BOSS_EMBER = {
  name: '잿불 대주술사',
  hp: 6000,
  radius: 1.6,
  staggerRatio: 0.1,
  /** 이 거리 범위를 유지하려 움직인다(m) */
  keepMin: 5,
  keepMax: 10,
  moveSpeed: 1.8,
  novaTrigger: 4,
  novaRadius: 4.5,
  novaWindup: ms(1000),
  novaDamage: 43,
  meteorCount: 3,
  meteorRadius: 2.2,
  meteorWindup: ms(1200),
  /** 운석 사이 간격(경고 시작·판정 모두). HAZARD.minResolveGap 이상 */
  meteorGap: ms(750),
  meteorDamage: 31,
  fissureCount: 3,
  fissureSpreadDeg: 35,
  fissureLength: 16,
  fissureWidth: 1.8,
  fissureWindup: ms(1300),
  fissureDamage: 39,
  attackCooldown: ms(1400),
  summonAt: [0.7, 0.4] as const,
  /** 소환 몹 종류(MobKind 번호) */
  summonKinds: [3, 3, 0, 0, 0] as const,
  summonRing: 6,
  ventsAt: 0.6,
} as const;
/** COMBAT-FEEL 3.1: 보스 동작 길이 */
export const BOSS_TIMING = {
  intro: ms(1500),
  stagger: ms(1500),
  dying: ms(1500),
  slamWindup: ms(1200),
  sweepWindup: ms(1000),
  strike: ms(350),
  attackCooldown: ms(1600),
  summon: ms(800),
} as const;

/** ECONOMY 6장: 던전 결과 보상(M1 가안) */
export const REWARD = { base: 200, perKill: 3, cardMin: 100, cardMax: 300 } as const;
/** ECONOMY 6장: 별 3개 = 클리어 + 5분 이내 + 체력 50% 이상 */
export const STARS = { timeTicks: ms(300_000), healthRatio: 0.5 } as const;

/** COMBAT-FEEL 4장 */
export const DAMAGE_NUMBERS = { lifeMs: 600, riseM: 0.8, critScale: 1.6, maxOnScreen: 12 } as const;
export const COMBO_COUNTER = { reset: ms(2500) } as const;
export const PRAISE_TIERS = [
  { min: 10, label: 'NICE!' },
  { min: 25, label: 'GREAT!' },
  { min: 50, label: 'SLICK!' },
  { min: 100, label: 'INSANE!!' },
] as const;

/** COMBAT-FEEL 5장: 합계 2.4초 */
export const AWAKEN = {
  gather: ms(600),
  flash: ms(100),
  cutin: ms(350),
  barrage: ms(900),
  recover: ms(450),
  slashes: 6,
  gatherTimeScale: 0.2,
  /** AwakenPhase(None, Gather, Flash, Cutin, Barrage, Recover)별 몹·보스 시간 배율. Recover는 시작값이고 recoverRampTicks 동안 1까지 선형 회복(COMBAT-FEEL 2.1) */
  worldScale: [1, 0.2, 0, 0, 0.2, 0.2] as const,
  recoverRampTicks: ms(450),
  /** 난무 타격은 히트스톱을 만들지 않는다 */
  hitstop: false,
  /** 전체 화면 섬광을 내는 난무 참격 번호(0부터). 섬광 단계 1회 + 이것 1회 = 각성당 2회 */
  fullFlashSlashes: [5] as const,
  pullRadius: 11,
  /** 모으기 단계 끌어당김 속도(m/s) */
  pullSpeed: 9,
  radius: 9,
  mult: 5.0,
  /** 게이지 충전: 타격 1회, 피격 1회 */
  gainPerHit: 0.006,
  gainPerHurt: 0.03,
} as const;
/** 영웅별 각성 난무(c022). 카엘 = AWAKEN 값 그대로, 라이라 = 폭풍 화살비(skills/lyra.ts LYRA_AWAKEN) */
const KAEL_AWAKEN_HITS = { hits: AWAKEN.slashes, radius: AWAKEN.radius, mult: AWAKEN.mult } as const;
const LYRA_AWAKEN_HITS = { hits: LYRA_AWAKEN.volleys, radius: LYRA_AWAKEN.radius, mult: LYRA_AWAKEN.mult } as const;

/** 저스트 회피: 영웅은 정상 속도, 세계만 느려지고 다음 공격의 첫 타격이 강화된다. */
export const JUST_DODGE = {
  window: ms(120),
  worldScale: 0.3,
  slowTicks: ms(300),
  counterTicks: ms(1000),
  postureMult: 2,
  awakenGain: AWAKEN.gainPerHit * 4,
} as const;

/** 광과민 안전(AV-SPEC, AGENTS.md): 섬광 1초 3회 이하, 1회 최대 100ms */
export const FLASH_SAFETY = { maxPerSecond: 3, maxFlashMs: 100, minGapMs: 334 } as const;

/** ART-BIBLE 4장 */
export const PALETTE = {
  warmStone: 0xe9d8b4,
  moss: 0x5e9a4a,
  skyTeal: 0x4fb6d0,
  navy: 0x1a2238,
  gold: 0xf2c14e,
  crimson: 0xff2a3d,
} as const;

/** 콘셉트(c-mob-sweep, ui-battle-hud): 높은 3/4 시점, 회전 없음 */
// 2026-10-13 콘셉트 비교: 19m에서는 영웅이 화면 높이의 약 1/9 → 14m로 당겨 약 1/4~1/3(ui-battle-hud)에 가깝게
/**
 * 카메라(2026-10-14 콘셉트 맞춤): concept/ui/ui-battle-hud처럼 낮은 각도·넓은 화각으로 원경까지 보이고 영웅은 화면 가운데 조금 아래.
 * 이전 값(피치 42°, 거리 12m, 화각 38°)은 위에서 내려다본 바닥만 보여 콘셉트와 달랐다.
 */
export const CAMERA = { pitchDeg: 28, distance: 12, fovDeg: 50, followSharpness: 10, lookAhead: 3.4 } as const;
/**
 * 플레이어 줌(마우스 휠): 0 = 기본 전투 구도, 1 = concept/combat/c-mob-sweep 같은 넓은 전장 구도.
 * 각도는 고정하고 거리·앞보기만 바꾼다(빌보드·데칼이 CAMERA.pitchDeg를 쓴다).
 */
export const CAMERA_ZOOM = {
  initial: 0, wheelStep: 0.25, sharpness: 8, farDistance: 19, farLookAhead: 3.2,
  /**
   * 근접 쪽 확장(2026-10-27 대표님 "보스전 카메라를 가까이"): 줌이 0보다 작으면 숄더뷰 쪽으로 더 당긴다. min = 가장 가까운 끝(-1).
   * 0..1은 예전 저장값과 같은 거리라 저장된 줌이 그대로 유지된다. -1에서 영웅~카메라 nearDistance(m), 앞보기 nearLookAhead(m),
   * 시선을 nearLookY(m)만큼 올리고 영웅을 화면 왼쪽으로 nearSide(m)만큼 비켜(숄더뷰: 정면 보스·적이 영웅 몸에 안 가린다) 피치를 nearPitch(도, 음수 = 낮게)만큼 눕힌다. 빌보드·바닥 데칼은 기본 피치 기준이라 작게 둔다.
   */
  min: -1, nearDistance: 5.6, nearLookAhead: 0.3, nearLookY: 1.2, nearPitch: -8, nearSide: 1.2,
  /** 보스 등장 직후 소개 구도를 지키는 시간(초). 그 뒤 플레이어 줌으로 풀린다 */
  bossIntroHold: 2.6,
} as const;

/** 웹 성능 예산(TECH 4장, AV-SPEC) */
export const BUDGET = { drawCalls: 150, vfxDrawCalls: 30, particles: 4000 } as const;

/**
 * 렌더 품질. low/mid/high는 ?quality·설정 화면에서 고르는 고정 프리셋이고, 설정이 '자동'이면
 * render/quality.ts의 사다리(QUALITY_LADDER)를 프레임 시간으로 오르내린다. 모바일 30fps 게이트는 실기기 p95로 판정한다(TECH 1장).
 */
export type QualityLevel = 'low' | 'mid' | 'high';
export interface QualityStep {
  readonly label: string;
  /** 해상도 배율 상한(실제 = min(devicePixelRatio, 이 값)) */
  readonly pixelRatio: number;
  /** 그림자 맵 해상도. 0이면 그림자 대신 발밑 블롭 */
  readonly shadowMap: number;
  readonly msaa: boolean;
  readonly bloomScale: number;
  /** 부팅 때만: 입자 풀 배율 */
  readonly particleScale: number;
  /** 이 카메라 거리(m) 안의 몹만 원본 메시, 나머지는 저폴리(LOD1). 0이면 모두 저폴리 */
  readonly mobLodNear: number;
  /** 원본 메시로 그리는 몹 수 상한(가까운 순) */
  readonly mobHdCap: number;
  /** 몹 외곽선: all 모두, near 원본 메시(가까운 몹)만, off 없음 */
  readonly mobOutline: 'all' | 'near' | 'off';
  readonly kaelOutline: boolean;
  /** 환경 장식 층(풀·꽃·빛줄기) */
  readonly decor: boolean;
  /** 부팅 때만: 환경 배치 밀도·환경 그림자 등급 */
  readonly env: QualityLevel;
}
export const RENDER_QUALITY: Record<QualityLevel, QualityStep> = {
  low: { label: 'low', pixelRatio: 1, shadowMap: 0, msaa: false, bloomScale: 0.5, particleScale: 0.5, mobLodNear: 16, mobHdCap: 10, mobOutline: 'off', kaelOutline: true, decor: true, env: 'low' },
  mid: { label: 'mid', pixelRatio: 1.5, shadowMap: 1024, msaa: false, bloomScale: 0.75, particleScale: 0.75, mobLodNear: 22, mobHdCap: 24, mobOutline: 'near', kaelOutline: true, decor: true, env: 'mid' },
  high: { label: 'high', pixelRatio: 2, shadowMap: 2048, msaa: true, bloomScale: 1, particleScale: 1, mobLodNear: 26, mobHdCap: 40, mobOutline: 'all', kaelOutline: true, decor: true, env: 'high' },
};

/**
 * 결투장(Dueling Grounds) 측정 적용(docs/analysis/DUELING-GROUNDS.md, 2026-10-14).
 * KO: 엘리트·방의 마지막 몹·보스를 쓰러뜨린 순간 히트스톱을 hitstop틱으로 올리고, 정지가 풀린 뒤 slowTicks(영웅 틱) 동안
 * 세계(몹·보스)만 worldScale배로 늦춘다. 영웅은 그대로 움직인다(조작 지연 없음). 각성 중에는 각성 연출이 우선이라 걸지 않는다.
 */
export const KO = {
  worldScale: 0.3, slowTicks: ms(250), hitstop: ms(90),
  /** 보스 결정타(던전 클리어 순간, 2026-10-27 「하나를 잡았다」 손맛): 정지 130ms + 느린 장면 450ms(0.3배). 섬광은 아니라 어둡게 하는 KO 화면이 이어진다 */
  bossSlowTicks: ms(450), bossHitstop: ms(130),
} as const;
/**
 * 푸시인 [화면 확대 배율, 들어가기 ms, 돌아오기 ms](실제 시간, 히트스톱 중에도 진행).
 * 결투장 측정: 1.02~1.08배를 50~67ms에 들어갔다가 돌아온다. 마무리타·스킬은 1.03배, KO는 1.07배. 설정 '타격 흔들림' 배율을 곱한다.
 * RPG-1(2026-10-15): 카메라 헌법(줌 펄스 금지)으로 렌더 카메라가 적용하지 않는다(docs/AV-SPEC.md). 값은 되돌릴 때를 위해 남긴다.
 */
export const PUSH_IN = { hit: [1.03, 60, 120], ko: [1.07, 67, 280] } as const;
/**
 * 클리어 연출 대기(2026-10-28 대표님 「보스를 처치한 장면을 먼저 보여 주고 CLEAR」): 마지막 적을 쓰러뜨린 뒤 곧바로 결과 화면으로 넘기지 않고,
 * 실제 틱(60Hz, 히트스톱 포함)으로 이 시간 동안 쓰러지는 장면·바닥 드롭·카메라 고정을 보여 준 다음에 결과(CLEAR)로 넘긴다.
 * bossTicks: 보스 결정타부터(정지 130ms+슬로 450ms 포함, 사망 연출 약 2초 뒤까지), roomTicks: 보스 없이 마지막 몹이 쓰러진 경우,
 * skipAfter: 이 시간이 지나면 화면을 눌러 건너뛸 수 있다
 */
export const CLEAR_HOLD = { bossTicks: ms(2300), roomTicks: ms(1900), skipAfter: ms(800) } as const;
/** KO 화면(실제 시간 ms): 어두워짐·붉은 테두리 세기 dark, 집중선 lines. 설정 '전투 연출' 배율을 곱한다(끔이면 없음) */
export const KO_SCREEN = { inMs: 60, holdMs: 260, outMs: 220, dark: 1, lines: 0.32 } as const;
